import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Cake, Loader2, Calendar, Gift, Clock, CheckCircle, XCircle,
  Settings, Users, PartyPopper, Bell
} from "lucide-react";

export default function CumpleanosPage() {
  const [proximosCumpleanos, setProximosCumpleanos] = useState([]);
  const [solicitudes, setSolicitudes] = useState([]);
  const [config, setConfig] = useState({
    dias_libres: 1,
    tipo_dia: "dia_exacto",
    requiere_solicitud: false,
    aplicar_automatico: true,
    notificar_cumpleanos: true,
    dias_anticipacion_notificacion: 3,
    mensaje_felicitacion: "",
    activo: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("proximos");
  const [showSolicitudDialog, setShowSolicitudDialog] = useState(false);
  const [solicitudForm, setSolicitudForm] = useState({ fecha_solicitada: "" });
  
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador", "gestion_humana"].includes(user?.role?.toLowerCase());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [proximosRes, solicitudesRes, configRes] = await Promise.all([
        axios.get(`${API}/cumpleanos/proximos?dias=30`, getAuthHeaders()),
        axios.get(`${API}/cumpleanos/solicitudes`, getAuthHeaders()),
        axios.get(`${API}/cumpleanos/configuracion`, getAuthHeaders())
      ]);
      setProximosCumpleanos(proximosRes.data);
      setSolicitudes(solicitudesRes.data);
      setConfig(configRes.data);
    } catch (err) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/cumpleanos/configuracion`, config, getAuthHeaders());
      toast.success("Configuración guardada");
    } catch (err) {
      toast.error("Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSolicitud = async () => {
    if (!solicitudForm.fecha_solicitada) {
      toast.error("Seleccione una fecha");
      return;
    }
    try {
      await axios.post(`${API}/cumpleanos/solicitudes`, solicitudForm, getAuthHeaders());
      toast.success("Solicitud creada");
      setShowSolicitudDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear solicitud");
    }
  };

  const handleApprove = async (solicitudId, approve) => {
    try {
      await axios.put(
        `${API}/cumpleanos/solicitudes/${solicitudId}/estado?estado=${approve ? 'aprobada' : 'rechazada'}`,
        {},
        getAuthHeaders()
      );
      toast.success(approve ? "Solicitud aprobada" : "Solicitud rechazada");
      fetchData();
    } catch (err) {
      toast.error("Error al actualizar estado");
    }
  };

  const getDaysLabel = (days) => {
    if (days === 0) return "¡Hoy!";
    if (days === 1) return "Mañana";
    return `En ${days} días`;
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="ml-0 md:ml-64 flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
                <Cake className="w-8 h-8 text-pink-500" />
                Día de Cumpleaños
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Gestión de días libres por cumpleaños
              </p>
            </div>
            {config.requiere_solicitud && (
              <Button onClick={() => setShowSolicitudDialog(true)} className="gap-2 bg-pink-600 hover:bg-pink-700">
                <Gift className="w-4 h-4" />
                Solicitar Mi Día
              </Button>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-pink-100 dark:bg-pink-900/30">
                    <PartyPopper className="w-6 h-6 text-pink-600 dark:text-pink-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">
                      {proximosCumpleanos.filter(c => c.dias_restantes <= 7).length}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Esta semana</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                    <Calendar className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">
                      {proximosCumpleanos.length}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Este mes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                    <Clock className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">
                      {solicitudes.filter(s => s.estado === "pendiente").length}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Pendientes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-white dark:bg-slate-800 border dark:border-slate-700 mb-4">
              <TabsTrigger value="proximos">Próximos Cumpleaños</TabsTrigger>
              <TabsTrigger value="solicitudes">Solicitudes</TabsTrigger>
              {isAdmin && <TabsTrigger value="configuracion">Configuración</TabsTrigger>}
            </TabsList>

            <TabsContent value="proximos">
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PartyPopper className="w-5 h-5 text-pink-500" />
                    Próximos 30 días
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-pink-500" />
                    </div>
                  ) : proximosCumpleanos.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">
                      No hay cumpleaños en los próximos 30 días
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {proximosCumpleanos.map((cumple, idx) => (
                        <div 
                          key={idx}
                          className={`flex items-center justify-between p-4 rounded-lg border dark:border-slate-700 ${
                            cumple.dias_restantes === 0 
                              ? 'bg-pink-50 dark:bg-pink-900/20 border-pink-200 dark:border-pink-800' 
                              : cumple.dias_restantes <= 3
                              ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                              : 'bg-slate-50 dark:bg-slate-700/50'
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white font-bold text-lg">
                              {cumple.nombre?.charAt(0) || "?"}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900 dark:text-white">{cumple.nombre}</p>
                              <p className="text-sm text-slate-500 dark:text-slate-400">{cumple.departamento}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge className={
                              cumple.dias_restantes === 0 
                                ? "bg-pink-500 text-white"
                                : cumple.dias_restantes <= 3
                                ? "bg-amber-500 text-white"
                                : "bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200"
                            }>
                              {getDaysLabel(cumple.dias_restantes)}
                            </Badge>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                              {new Date(cumple.fecha_cumpleanos).toLocaleDateString('es', { day: 'numeric', month: 'short' })}
                              {cumple.edad && ` · ${cumple.edad} años`}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="solicitudes">
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader>
                  <CardTitle>Solicitudes de Día de Cumpleaños</CardTitle>
                </CardHeader>
                <CardContent>
                  {solicitudes.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No hay solicitudes</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 dark:bg-slate-900">
                          <tr>
                            <th className="text-left p-3 font-medium text-slate-600 dark:text-slate-300">Empleado</th>
                            <th className="text-left p-3 font-medium text-slate-600 dark:text-slate-300">Fecha Solicitada</th>
                            <th className="text-left p-3 font-medium text-slate-600 dark:text-slate-300">Estado</th>
                            {isAdmin && <th className="text-right p-3 font-medium text-slate-600 dark:text-slate-300">Acciones</th>}
                          </tr>
                        </thead>
                        <tbody className="divide-y dark:divide-slate-700">
                          {solicitudes.map(sol => (
                            <tr key={sol.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                              <td className="p-3 font-medium text-slate-900 dark:text-white">{sol.user_name}</td>
                              <td className="p-3 text-slate-600 dark:text-slate-300">{sol.fecha_solicitada}</td>
                              <td className="p-3">
                                <Badge className={
                                  sol.estado === "aprobada" ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" :
                                  sol.estado === "rechazada" ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" :
                                  "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                                }>
                                  {sol.estado === "aprobada" ? "Aprobada" : sol.estado === "rechazada" ? "Rechazada" : "Pendiente"}
                                </Badge>
                              </td>
                              {isAdmin && sol.estado === "pendiente" && (
                                <td className="p-3 text-right">
                                  <Button size="sm" variant="ghost" className="text-green-600" onClick={() => handleApprove(sol.id, true)}>
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleApprove(sol.id, false)}>
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {isAdmin && (
              <TabsContent value="configuracion">
                <Card className="dark:bg-slate-800 dark:border-slate-700">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Settings className="w-5 h-5" />
                      Configuración de Cumpleaños
                    </CardTitle>
                    <CardDescription>
                      Define las políticas para el día libre de cumpleaños
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base">Módulo Activo</Label>
                        <p className="text-sm text-slate-500">Habilitar día libre por cumpleaños</p>
                      </div>
                      <Switch
                        checked={config.activo}
                        onCheckedChange={(v) => setConfig({...config, activo: v})}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Días Libres por Cumpleaños</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.5"
                          value={config.dias_libres}
                          onChange={(e) => setConfig({...config, dias_libres: parseFloat(e.target.value)})}
                          className="dark:bg-slate-700 dark:border-slate-600"
                        />
                      </div>
                      <div>
                        <Label>Tipo de Día</Label>
                        <select
                          value={config.tipo_dia}
                          onChange={(e) => setConfig({...config, tipo_dia: e.target.value})}
                          className="w-full p-2 rounded-md border dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                        >
                          <option value="dia_exacto">Día exacto</option>
                          <option value="dia_habil_cercano">Día hábil más cercano</option>
                          <option value="elegir_semana">Elegir en la semana</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base">Requiere Solicitud</Label>
                        <p className="text-sm text-slate-500">El empleado debe solicitar el día</p>
                      </div>
                      <Switch
                        checked={config.requiere_solicitud}
                        onCheckedChange={(v) => setConfig({...config, requiere_solicitud: v})}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base">Aplicar Automático</Label>
                        <p className="text-sm text-slate-500">Registrar día libre automáticamente</p>
                      </div>
                      <Switch
                        checked={config.aplicar_automatico}
                        onCheckedChange={(v) => setConfig({...config, aplicar_automatico: v})}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base">Notificar Cumpleaños</Label>
                        <p className="text-sm text-slate-500">Enviar notificaciones de cumpleaños próximos</p>
                      </div>
                      <Switch
                        checked={config.notificar_cumpleanos}
                        onCheckedChange={(v) => setConfig({...config, notificar_cumpleanos: v})}
                      />
                    </div>

                    {config.notificar_cumpleanos && (
                      <div>
                        <Label>Días de Anticipación para Notificación</Label>
                        <Input
                          type="number"
                          min="1"
                          max="30"
                          value={config.dias_anticipacion_notificacion}
                          onChange={(e) => setConfig({...config, dias_anticipacion_notificacion: parseInt(e.target.value)})}
                          className="dark:bg-slate-700 dark:border-slate-600"
                        />
                      </div>
                    )}

                    <div>
                      <Label>Mensaje de Felicitación</Label>
                      <textarea
                        value={config.mensaje_felicitacion}
                        onChange={(e) => setConfig({...config, mensaje_felicitacion: e.target.value})}
                        placeholder="¡Feliz cumpleaños! Esperamos que tengas un excelente día."
                        className="w-full p-3 rounded-md border dark:bg-slate-700 dark:border-slate-600 dark:text-white min-h-[80px]"
                      />
                    </div>

                    <Button onClick={handleSaveConfig} disabled={saving} className="w-full">
                      {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Guardar Configuración
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </Tabs>
        </div>

        {/* Solicitud Dialog */}
        <Dialog open={showSolicitudDialog} onOpenChange={setShowSolicitudDialog}>
          <DialogContent className="dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-pink-500" />
                Solicitar Día de Cumpleaños
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Fecha para tomar el día</Label>
                <Input
                  type="date"
                  value={solicitudForm.fecha_solicitada}
                  onChange={(e) => setSolicitudForm({...solicitudForm, fecha_solicitada: e.target.value})}
                  className="dark:bg-slate-700 dark:border-slate-600"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSolicitudDialog(false)}>Cancelar</Button>
              <Button onClick={handleCreateSolicitud} className="bg-pink-600 hover:bg-pink-700">Solicitar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
